import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { Language } from "@/lib/i18n/translations";
import { stripLangPrefix, buildLocalizedUrl } from "@/hooks/useLocalePath";
import { getPageMeta } from "@/lib/seo/pageMeta";

const BASE_URL = "https://schemasgen.com";

/**
 * Manages dynamic SEO tags: hreflang alternates, self-referencing canonical,
 * document language, title, and meta descriptions.
 */
export const SEOHead = () => {
  const location = useLocation();
  const { language, t } = useLanguage();

  useEffect(() => {
    // Get base English path (strips lang prefix + delocalizes slugs)
    const basePath = stripLangPrefix(location.pathname);
    const currentUrl = buildLocalizedUrl(basePath, language, BASE_URL);

    // === Self-referencing canonical ===
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', currentUrl);

    // === Hreflang tags ===
    document.querySelectorAll('link[rel="alternate"][hreflang]').forEach(el => el.remove());

    const hreflangs: Array<{ hreflang: string; lang: Language }> = [
      { hreflang: 'en', lang: 'en' },
      { hreflang: 'fr', lang: 'fr' },
      { hreflang: 'es', lang: 'es' },
      { hreflang: 'x-default', lang: 'en' },
    ];

    hreflangs.forEach(({ hreflang, lang }) => {
      const link = document.createElement('link');
      link.setAttribute('rel', 'alternate');
      link.setAttribute('hreflang', hreflang);
      link.setAttribute('href', buildLocalizedUrl(basePath, lang, BASE_URL));
      document.head.appendChild(link);
    });

    // === Document lang ===
    document.documentElement.lang = language;

    // === Title & Meta (route-specific when available) ===
    const pageMeta = getPageMeta(basePath, language);
    const title = pageMeta?.title ?? t.meta.title;
    const description = pageMeta?.description ?? t.meta.description;
    document.title = title;

    const updateMeta = (selector: string, content: string) => {
      const el = document.querySelector(selector);
      if (el) el.setAttribute('content', content);
    };

    updateMeta('meta[name="description"]', description);
    updateMeta('meta[property="og:title"]', title);
    updateMeta('meta[property="og:description"]', description);
    updateMeta('meta[name="twitter:title"]', title);
    updateMeta('meta[name="twitter:description"]', description);
    updateMeta('meta[name="twitter:url"]', currentUrl);
    updateMeta('meta[property="og:url"]', currentUrl);

    const localeMap: Record<Language, string> = { en: 'en_US', fr: 'fr_FR', es: 'es_ES' };
    updateMeta('meta[property="og:locale"]', localeMap[language]);

    // Cleanup hreflang tags on unmount
    return () => {
      document.querySelectorAll('link[rel="alternate"][hreflang]').forEach(el => el.remove());
    };
  }, [location.pathname, language, t]);

  return null;
};
